import { createConsoleHandoffCode } from "@entities/user-tokens";

import { openBrowserWindow } from "../office/openBrowserWindow";

/**
 * Web コンソール base URL。
 *
 * webpack の DefinePlugin / 環境変数経由で注入される値ではなく、ビルド時に webpack 設定経由で
 * `process.env.IM_CONSOLE_BASE_URL` を埋め込む想定。ここでは未定義の場合に明示エラーにする。
 */
function consoleBaseUrl(): string {
    const url = process.env.IM_CONSOLE_BASE_URL;
    if (!url) {
        throw new Error("IM_CONSOLE_BASE_URL is not configured");
    }
    return url.replace(/\/$/, "");
}

/**
 * 遷移先を自オリジン配下の相対パスのみに正規化する（bridge 側 `safeReturnPath` と対称の防御）。
 * 絶対 URL・protocol-relative（`//host`）を弾く。
 */
function normalizeNext(next: string | undefined): string | null {
    if (!next) return null;
    if (!next.startsWith("/") || next.startsWith("//")) return null;
    return next;
}

/**
 * アドインから Web コンソールを開く高レベル操作。
 *
 * - **ログイン済み**: サーバーから handoff コードを取得し、`${IM_CONSOLE_BASE_URL}/bridge?code=...`
 *   を OS の既定ブラウザで開く（SSO 引き継ぎ）。`opts.next` を渡すと bridge が Cookie 発行後に
 *   その自オリジンパスへ着地させる（UC-5 の指定リソース着地。例 `/slide-studio?open=task:<id>`）。
 * - **未ログイン / 再認証要求 (handoff が 401 等で取得不可)**: コードなしで `${IM_CONSOLE_BASE_URL}`
 *   （`opts.next` があればそのパス）を開く。コンソール側の認証ゲート（`(authenticated)/layout.tsx`）が
 *   未認証を検知して `login-start` へ自動 redirect し、ログイン後に元のパスへ戻す。
 *
 * すなわち handoff は **best-effort**。取得できなくても必ずコンソールを開く（実行ケースを欠かさない）。
 * `IM_CONSOLE_BASE_URL` 未設定時のみ例外。**要 taskpane ランタイム実行**（cookie は taskpane 側のみ）。
 */
export async function openConsole(opts: { next?: string } = {}): Promise<void> {
    const base = consoleBaseUrl();
    const next = normalizeNext(opts.next);
    try {
        const { handoffCode } = await createConsoleHandoffCode();
        const nextParam = next ? `&next=${encodeURIComponent(next)}` : "";
        openBrowserWindow(`${base}/bridge?code=${encodeURIComponent(handoffCode)}${nextParam}`);
    } catch {
        // 未ログイン/再認証要求/取得失敗時はコードなしでコンソールを開く（→ コンソール側でログイン）。
        openBrowserWindow(next ? `${base}${next}` : base);
    }
}
